import { useEffect, useState } from "react";
import { treasureHuntApi } from "../../services/treasureHunt.api";
import { MODAL_STYLES } from "../../constants/modal-styles";
import { ModalButton } from "./ModalButton";
import { ActivationCode } from "../../types/treasure-hunt";

interface PromoCodeModalProps {
  isOpen: boolean;
  language: "ka" | "en";
  onSubmit: (code: string) => void;
}

export const PromoCodeModal = ({
  isOpen,
  language,
  onSubmit,
}: PromoCodeModalProps) => {
  const [code, setPromoCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPromoCode("");
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await treasureHuntApi.validateActivationCode(
        trimmedCode
      );

      if (response.success && response.data) {
        const codeData = response.data as ActivationCode;

        // Проверяем, использован ли код
        if (codeData.isUsed) {
          setError(
            language === "ka"
              ? "ეს კოდი უკვე გამოყენებულია"
              : "This code has already been used"
          );
          return;
        }

        onSubmit(trimmedCode);
      } else {
        // Обрабатываем сообщение с сервера и показываем локализованное
        const serverMessage = response.message || "";
        if (serverMessage.includes("не найден") || serverMessage.includes("not found")) {
          setError(
            language === "ka"
              ? "კოდი ვერ მოიძებნა"
              : "Code not found"
          );
        } else {
          setError(
            language === "ka"
              ? "კოდის შემოწმების შეცდომა"
              : "Error validating code"
          );
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "";
      // Обрабатываем ошибку и показываем локализованное сообщение
      if (errorMessage.includes("не найден") || errorMessage.includes("not found")) {
        setError(
          language === "ka"
            ? "კოდი ვერ მოიძებნა"
            : "Code not found"
        );
      } else {
        setError(
          language === "ka"
            ? "კოდის შემოწმების შეცდომა"
            : "Error validating code"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const content = {
    ka: {
      title: "შეიყვანეთ კოდი",
      placeholder: "შეიყვანეთ კოდი",
      button: "გამოყენება",
      errorNotFound: "კოდი ვერ მოიძებნა",
      errorUsed: "კოდი უკვე გამოყენებულია",
      errorGeneric: "კოდის შემოწმების შეცდომა",
    },
    en: {
      title: "Enter code",
      placeholder: "Enter code",
      button: "Apply",
      errorNotFound: "Code not found",
      errorUsed: "Code already used",
      errorGeneric: "Error validating code",
    },
  };

  const text = content[language] || content.ka;

  return (
    <div className={MODAL_STYLES.bottomOverlay}>
      <div
        className={MODAL_STYLES.bottomModal}
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: MODAL_STYLES.fontFamily,
          maxWidth: "400px",
        }}
      >
        <div className={MODAL_STYLES.content}>
          <h2 className="text-sm sm:text-base font-bold text-amber-900 mb-3 text-center">
            {text.title}
          </h2>

          <div className="flex flex-col  items-center gap-2">
            <input
              type="text"
              value={code}
              onChange={(e) => {
                setPromoCode(e.target.value);
                setError(null);
              }}
              placeholder={text.placeholder}
              className="flex-1 px-3 py-2 h-9 rounded-lg bg-amber-50 text-amber-900 border-2 border-amber-700 focus:outline-none focus:border-amber-900 focus:bg-white transition-colors text-xs placeholder-amber-500"
              style={{ fontFamily: "MinecraftGeorgian, monospace" }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isLoading) handleSubmit();
              }}
              disabled={isLoading}
              autoFocus
            />
            <ModalButton
              onClick={handleSubmit}
              disabled={isLoading}
              fullWidth={false}
            >
              <span className="text-xs sm:text-sm">
                {isLoading
                  ? language === "ka"
                    ? "შემოწმება..."
                    : "Checking..."
                  : text.button}
              </span>
            </ModalButton>
          </div>

          {error && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-red-100 border-2 border-red-500">
              <p
                className="text-red-700 text-xs text-center"
                style={{ fontFamily: MODAL_STYLES.fontFamily }}
              >
                {error}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
